/** Product-default entity caps. Per-instance overrides live in `instance_feature_limit_overrides`. */

export const FEATURE_LIMIT_KEYS = [
  'customRolePresets',
  'checklists',
  'inboxAccounts',
  'employeeDocumentTypes',
  'pipelines',
  'pipelinePhases',
  'activityTypes',
  'vehicles',
  'leadCategories',
] as const;

export type FeatureLimitKey = (typeof FEATURE_LIMIT_KEYS)[number];

export const INSTANCE_FEATURE_LIMITS: Record<FeatureLimitKey, number> = {
  customRolePresets: 5,
  checklists: 20,
  inboxAccounts: 3,
  employeeDocumentTypes: 12,
  pipelines: 5,
  pipelinePhases: 12,
  activityTypes: 15,
  vehicles: 15,
  leadCategories: 12,
};

/** Kanban / normalize hard cap — overrides cannot exceed this. */
export const MAX_PIPELINE_PHASES_HARD = 24;

/** UX cap per checklist template (not an entitlement). */
export const CHECKLIST_QUESTIONS_MAX = 40;

export const FEATURE_LIMIT_ERROR_CODE = 'feature_limit_reached' as const;

export const FEATURE_LIMIT_NOUN_PLURAL: Record<FeatureLimitKey, string> = {
  customRolePresets: 'Rollen',
  checklists: 'Checklisten',
  inboxAccounts: 'Postfächer',
  employeeDocumentTypes: 'Dokumenttypen',
  pipelines: 'Pipelines',
  pipelinePhases: 'Phasen',
  activityTypes: 'Aktivitätstypen',
  vehicles: 'Fahrzeuge',
  leadCategories: 'Kategorien',
};

export const FEATURE_LIMIT_CONTACT_EMAIL = 'dennis@meisterpfad.de';

export type FeatureLimitUsage = {
  used: number;
  max: number;
};

export type FeatureLimitsMap = Record<FeatureLimitKey, FeatureLimitUsage>;

export function isFeatureLimitKey(value: unknown): value is FeatureLimitKey {
  return typeof value === 'string' && (FEATURE_LIMIT_KEYS as readonly string[]).includes(value);
}

export function defaultFeatureLimit(key: FeatureLimitKey): number {
  return INSTANCE_FEATURE_LIMITS[key];
}

/** Override wins when a positive integer; pipeline phases stay within the hard Kanban cap. */
export function resolveEffectiveMax(key: FeatureLimitKey, overrideValue: unknown): number {
  const fallback = defaultFeatureLimit(key);
  const n = typeof overrideValue === 'number' ? overrideValue : Number(overrideValue);
  const resolved = Number.isInteger(n) && n > 0 ? n : fallback;
  if (key === 'pipelinePhases') return Math.min(resolved, MAX_PIPELINE_PHASES_HARD);
  return resolved;
}

/**
 * Allow writes that stay at or below the cap, or that do not grow a grandfathered
 * over-cap inventory (`desired <= max(max, used)`).
 */
export function isDesiredCountAllowed(desired: number, used: number, max: number): boolean {
  if (!Number.isFinite(desired) || !Number.isFinite(used) || !Number.isFinite(max)) return false;
  return desired <= Math.max(max, used);
}

export function isAtFeatureLimit(used: number, max: number): boolean {
  return used >= max;
}

/** Last remaining slot, or at least 80% used — not yet blocked. */
export function isNearFeatureLimit(used: number, max: number): boolean {
  if (!Number.isFinite(used) || !Number.isFinite(max) || max <= 0) return false;
  if (used >= max || used < 0) return false;
  return used >= max - 1 || used / max >= 0.8;
}

export function formatFeatureLimitUsage(key: FeatureLimitKey, used: number, max: number): string {
  return `${used} / ${max} ${FEATURE_LIMIT_NOUN_PLURAL[key]}`;
}

export function featureLimitReachedMessage(key: FeatureLimitKey, used: number, max: number): string {
  return `Limit erreicht (${used}/${max}). Für weitere ${FEATURE_LIMIT_NOUN_PLURAL[key]} kontaktiere uns.`;
}

export function featureLimitReachedHint(key: FeatureLimitKey, used: number, max: number): string {
  return `${featureLimitReachedMessage(key, used, max)} ${FEATURE_LIMIT_CONTACT_EMAIL}`;
}

export class FeatureLimitError extends Error {
  readonly code = FEATURE_LIMIT_ERROR_CODE;
  readonly status = 403;
  readonly key: FeatureLimitKey;
  readonly used: number;
  readonly max: number;

  constructor(key: FeatureLimitKey, used: number, max: number) {
    super(featureLimitReachedMessage(key, used, max));
    this.name = 'FeatureLimitError';
    this.key = key;
    this.used = used;
    this.max = max;
  }

  toJSON(): {
    error: typeof FEATURE_LIMIT_ERROR_CODE;
    message: string;
    key: FeatureLimitKey;
    used: number;
    max: number;
  } {
    return {
      error: this.code,
      message: this.message,
      key: this.key,
      used: this.used,
      max: this.max,
    };
  }
}

export function isFeatureLimitError(err: unknown): err is FeatureLimitError {
  if (err instanceof FeatureLimitError) return true;
  if (!err || typeof err !== 'object') return false;
  const rec = err as { code?: unknown; key?: unknown; used?: unknown; max?: unknown };
  return rec.code === FEATURE_LIMIT_ERROR_CODE && isFeatureLimitKey(rec.key);
}

export function countsTowardCustomRolePreset(row: {
  is_system_fork?: boolean;
  is_hidden?: boolean;
}): boolean {
  return row.is_system_fork !== true && row.is_hidden !== true;
}

export function countsTowardActiveVehicle(row: { is_deleted?: boolean }): boolean {
  return row.is_deleted !== true;
}

export function countsTowardCustomDocumentType(row: { is_system?: boolean }): boolean {
  return row.is_system !== true;
}

export function emptyFeatureLimitsMap(): FeatureLimitsMap {
  return FEATURE_LIMIT_KEYS.reduce((acc, key) => {
    acc[key] = { used: 0, max: defaultFeatureLimit(key) };
    return acc;
  }, {} as FeatureLimitsMap);
}
